import type { ReactNode, RefObject } from "react";
import { AddressAutocompleteInput } from "../../../lib/addressAutocomplete.js";
import { inp, Field, type FieldAccess, FieldGrid, CollapsibleSection } from "../sectionShared.js";

type InputProps = { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled?: boolean; style: React.CSSProperties };

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  canView: (field: string) => boolean;
  inputProps: (field: string, value: string, onChange: (v: string) => void) => InputProps;
  pendingBadge: (field: string) => ReactNode;
  isCreate: boolean;
  permissions: Record<string, FieldAccess>;
  selectedOfficeCountry: string;
  isPincodeInvalid: (v: string, country: string) => boolean;

  permanentAddress: string; setPermanentAddress: (v: string) => void;
  permanentAddressLine2: string; setPermanentAddressLine2: (v: string) => void;
  permanentCity: string; setPermanentCity: (v: string) => void;
  permanentState: string; setPermanentState: (v: string) => void;
  permanentDistrict: string; setPermanentDistrict: (v: string) => void;
  permanentPincode: string; setPermanentPincode: (v: string) => void;
  setPermanentLat: (v: number | null) => void;
  setPermanentLng: (v: number | null) => void;

  residentialAddress: string; setResidentialAddress: (v: string) => void;
  residentialAddressLine2: string; setResidentialAddressLine2: (v: string) => void;
  residentialCity: string; setResidentialCity: (v: string) => void;
  residentialState: string; setResidentialState: (v: string) => void;
  residentialDistrict: string; setResidentialDistrict: (v: string) => void;
  residentialPincode: string; setResidentialPincode: (v: string) => void;
  setResidentialLat: (v: number | null) => void;
  setResidentialLng: (v: number | null) => void;

  sameAsPermanent: boolean; setSameAsPermanent: (v: boolean) => void;
}

export function AddressSection({
  sectionRef, canView, inputProps, pendingBadge, isCreate, permissions, selectedOfficeCountry, isPincodeInvalid,
  permanentAddress, setPermanentAddress, permanentAddressLine2, setPermanentAddressLine2,
  permanentCity, setPermanentCity, permanentState, setPermanentState,
  permanentDistrict, setPermanentDistrict, permanentPincode, setPermanentPincode,
  setPermanentLat, setPermanentLng,
  residentialAddress, setResidentialAddress, residentialAddressLine2, setResidentialAddressLine2,
  residentialCity, setResidentialCity, residentialState, setResidentialState,
  residentialDistrict, setResidentialDistrict, residentialPincode, setResidentialPincode,
  setResidentialLat, setResidentialLng,
  sameAsPermanent, setSameAsPermanent,
}: Props) {
  const isUS = selectedOfficeCountry === "US" || selectedOfficeCountry === "USA";

  return (
    <CollapsibleSection id="address" sectionRef={sectionRef} title="Address">
      <FieldGrid cols="two" gap="lg">
        {canView("permanentAddress") && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <p style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-surface-700)", margin: 0 }}>Permanent</p>
            <Field label="Address Line 1">
              {isCreate || (permissions["permanentAddress"]?.canEdit === "DIRECT") ? (
                <AddressAutocompleteInput
                  value={permanentAddress}
                  onChange={setPermanentAddress}
                  onResolve={(a) => {
                    setPermanentAddress(a.addressLine1);
                    setPermanentAddressLine2(a.addressLine2 ?? "");
                    setPermanentCity(a.city ?? "");
                    setPermanentState(a.state ?? "");
                    setPermanentDistrict(a.district ?? "");
                    setPermanentPincode(a.pincode ?? "");
                    setPermanentLat(a.lat);
                    setPermanentLng(a.lng);
                  }}
                  placeholder="Start typing address…"
                  style={inp}
                />
              ) : (
                <input {...inputProps("permanentAddress", permanentAddress, setPermanentAddress)} />
              )}
              {pendingBadge("permanentAddress")}
            </Field>
            <Field label="Address Line 2">
              <input {...inputProps("permanentAddressLine2", permanentAddressLine2, setPermanentAddressLine2)} placeholder="Apartment, floor, landmark…" />
            </Field>
            <FieldGrid cols="two" gap="sm">
              <Field label="City">
                <input {...inputProps("permanentCity", permanentCity, setPermanentCity)} />
              </Field>
              <Field label="District">
                <input {...inputProps("permanentDistrict", permanentDistrict, setPermanentDistrict)} />
              </Field>
              <Field label="State">
                <input {...inputProps("permanentState", permanentState, setPermanentState)} />
              </Field>
              <Field label="Pincode">
                <input {...inputProps("permanentPincode", permanentPincode, setPermanentPincode)} placeholder={isUS ? "5-digit ZIP" : "6-digit pincode"}
                  style={{ ...inputProps("permanentPincode", permanentPincode, setPermanentPincode).style, ...(isPincodeInvalid(permanentPincode, selectedOfficeCountry) ? { borderColor: "var(--color-error)" } : {}) }} />
                {isPincodeInvalid(permanentPincode, selectedOfficeCountry) && <div style={{ fontSize: 11, color: "var(--color-error)", marginTop: 3 }}>{isUS ? "5-digit ZIP code" : "6 digits, cannot start with 0"}</div>}
              </Field>
            </FieldGrid>
          </div>
        )}
        {canView("residentialAddress") && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <p style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-surface-700)", margin: 0 }}>Residential</p>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-surface-600)", cursor: "pointer" }}>
                <input type="checkbox" checked={sameAsPermanent} onChange={e => setSameAsPermanent(e.target.checked)} />
                Same as Permanent
              </label>
            </div>
            <Field label="Address Line 1">
              {!sameAsPermanent && (isCreate || (permissions["residentialAddress"]?.canEdit === "DIRECT")) ? (
                <AddressAutocompleteInput
                  value={residentialAddress}
                  onChange={setResidentialAddress}
                  onResolve={(a) => {
                    setResidentialAddress(a.addressLine1);
                    setResidentialAddressLine2(a.addressLine2 ?? "");
                    setResidentialCity(a.city ?? "");
                    setResidentialState(a.state ?? "");
                    setResidentialDistrict(a.district ?? "");
                    setResidentialPincode(a.pincode ?? "");
                    setResidentialLat(a.lat);
                    setResidentialLng(a.lng);
                  }}
                  placeholder="Start typing address…"
                  style={inp}
                />
              ) : (
                <input {...inputProps("residentialAddress", residentialAddress, setResidentialAddress)} disabled={sameAsPermanent} />
              )}
              {pendingBadge("residentialAddress")}
            </Field>
            <Field label="Address Line 2">
              <input {...inputProps("residentialAddressLine2", residentialAddressLine2, setResidentialAddressLine2)} placeholder="Apartment, floor, landmark…" disabled={sameAsPermanent} />
            </Field>
            <FieldGrid cols="two" gap="sm">
              <Field label="City">
                <input {...inputProps("residentialCity", residentialCity, setResidentialCity)} disabled={sameAsPermanent} />
              </Field>
              <Field label="District">
                <input {...inputProps("residentialDistrict", residentialDistrict, setResidentialDistrict)} disabled={sameAsPermanent} />
              </Field>
              <Field label="State">
                <input {...inputProps("residentialState", residentialState, setResidentialState)} disabled={sameAsPermanent} />
              </Field>
              <Field label="Pincode">
                <input {...inputProps("residentialPincode", residentialPincode, setResidentialPincode)} placeholder={isUS ? "5-digit ZIP" : "6-digit pincode"}
                  disabled={sameAsPermanent}
                  style={{ ...inputProps("residentialPincode", residentialPincode, setResidentialPincode).style, ...(isPincodeInvalid(residentialPincode, selectedOfficeCountry) ? { borderColor: "var(--color-error)" } : {}) }} />
                {isPincodeInvalid(residentialPincode, selectedOfficeCountry) && <div style={{ fontSize: 11, color: "var(--color-error)", marginTop: 3 }}>{isUS ? "5-digit ZIP code" : "6 digits, cannot start with 0"}</div>}
              </Field>
            </FieldGrid>
          </div>
        )}
      </FieldGrid>
    </CollapsibleSection>
  );
}
